/**
 * Date Utilities - Fonctions standardisées pour éviter les bugs DST
 * 
 * IMPORTANT: Utiliser ces fonctions partout au lieu de manipuler les dates directement
 * FORMAT FRANÇAIS: JJ/MM/AAAA avec fuseau horaire Europe/Paris
 */

// Constantes pour le formatage français
const FRENCH_MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
];

const FRENCH_DAYS = [
  'dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'
];

const PARIS_TZ = 'Europe/Paris';

/**
 * Obtenir la date du jour normalisée (midi pour éviter DST)
 */
export const getToday = () => {
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  return now;
};

/**
 * Formater une date en YYYY-MM-DD (ISO)
 */
export const formatDateISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Générer une clé unique pour un mois
 */
export const getMonthKey = (date) => {
  return `${date.getFullYear()}-${date.getMonth()}`;
};

/**
 * Comparer deux dates (même mois et année)
 */
export const isSameMonth = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth();
};

/**
 * Comparer deux dates (même jour)
 */
export const isSameDay = (date1, date2) => {
  return formatDateISO(date1) === formatDateISO(date2);
};

/**
 * Ajouter des jours à une date (safe pour DST)
 */
export const addDays = (date, days) => {
  const result = new Date(date.getTime());
  result.setDate(result.getDate() + days);
  result.setHours(12, 0, 0, 0); // Force midi
  return result;
};

/**
 * Obtenir le lundi de la semaine d'une date
 */
export const getMonday = (date) => {
  const d = new Date(date.getTime());
  d.setHours(12, 0, 0, 0);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setDate(diff);
  monday.setHours(12, 0, 0, 0);
  return monday;
};

/**
 * Générer les 7 jours d'une semaine (safe pour DST)
 */
export const getWeekDays = (startDate) => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(startDate, i));
  }
  return days;
};

/**
 * Obtenir le premier jour d'un mois
 */
export const getFirstDayOfMonth = (year, month) => {
  const date = new Date(year, month, 1);
  date.setHours(12, 0, 0, 0);
  return date;
};

/**
 * Obtenir la date du mois/année (normalisée)
 */
export const getDateFromMonthYear = (year, month) => {
  return getFirstDayOfMonth(parseInt(year), parseInt(month));
};

/**
 * Parser une date ISO string en Date object (normalisé)
 */
export const parseDateISO = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setHours(12, 0, 0, 0);
  return date;
};

/**
 * Obtenir tous les jours d'un mois donné
 */
export const getMonthDays = (year, month) => {
  const days = [];
  const firstDay = getFirstDayOfMonth(year, month);
  const lastDay = new Date(year, month + 1, 0); // Dernier jour du mois
  const numDays = lastDay.getDate();
  
  for (let i = 0; i < numDays; i++) {
    days.push(addDays(firstDay, i));
  }
  
  return days;
};

/**
 * Ajouter/soustraire des mois à une date
 */
export const addMonths = (date, months) => {
  const result = new Date(date.getTime());
  result.setMonth(result.getMonth() + months);
  result.setHours(12, 0, 0, 0);
  return result;
};

/**
 * Obtenir le premier jour du mois en cours
 */
export const getFirstDayOfCurrentMonth = () => {
  const today = getToday();
  return getFirstDayOfMonth(today.getFullYear(), today.getMonth());
};

/**
 * Détecter si on est sur mobile
 */
export const isMobile = () => {
  return window.innerWidth <= 768;
};

/**
 * Formater une date en français (JJ/MM/AAAA)
 * @param {Date|string} date - Date object ou string ISO
 * @returns {string} Date formatée en français (ex: "15/11/2025")
 */
export const formatDateFrench = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseDateISO(date) : date;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Formater une date avec le jour de la semaine en français
 * @param {Date|string} date - Date object ou string ISO
 * @returns {string} Date formatée (ex: "lundi 15 novembre 2025")
 */
export const formatDateLongFrench = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseDateISO(date) : date;
  const dayName = FRENCH_DAYS[d.getDay()];
  const day = d.getDate();
  const monthName = FRENCH_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `${dayName} ${day} ${monthName} ${year}`;
};

/**
 * Obtenir le nom du mois en français
 * @param {Date|number} dateOrMonth - Date object ou numéro du mois (0-11)
 * @returns {string} Nom du mois en français
 */
export const getMonthNameFrench = (dateOrMonth) => {
  if (dateOrMonth instanceof Date) {
    return FRENCH_MONTHS[dateOrMonth.getMonth()];
  }
  return FRENCH_MONTHS[dateOrMonth] || '';
};

/**
 * Obtenir le nom du jour en français
 * @param {Date} date - Date object
 * @returns {string} Nom du jour en français
 */
export const getDayNameFrench = (date) => {
  return FRENCH_DAYS[date.getDay()];
};

/**
 * Formater une date avec le fuseau horaire Paris
 * @param {Date|string} date - Date object ou string ISO
 * @returns {Date} Date avec le fuseau horaire Paris
 */
export const toParisTime = (date) => {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  
  // Obtenir la date en fuseau horaire Paris
  const parisTime = new Date(d.toLocaleString('en-US', { timeZone: PARIS_TZ }));
  parisTime.setHours(12, 0, 0, 0); // Normaliser à midi
  return parisTime;
};

/**
 * Formater une période (date début - date fin) en français
 * @param {Date|string} startDate - Date de début
 * @param {Date|string} endDate - Date de fin
 * @returns {string} Période formatée (ex: "01/11/2025 - 30/11/2025")
 */
export const formatPeriodFrench = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  return `${formatDateFrench(startDate)} - ${formatDateFrench(endDate)}`;
};

/**
 * Formater l'heure en français (HH:MM)
 * @param {string} time - Heure au format HH:MM
 * @returns {string} Heure formatée
 */
export const formatTimeFrench = (time) => {
  if (!time) return '';
  return time; // Déjà au bon format HH:MM
};

/**
 * Obtenir la date actuelle au fuseau horaire de Paris
 * @returns {Date} Date actuelle à Paris (normalisée à midi)
 */
export const getTodayParis = () => {
  const parisTime = new Date(new Date().toLocaleString('en-US', { timeZone: PARIS_TZ }));
  parisTime.setHours(12, 0, 0, 0);
  return parisTime;
};
